// src/components/Warehouse/WarehouseItemList.js

import React, { useState, useEffect, useMemo, useCallback } from "react";
import PropTypes from "prop-types";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import MDBox from "components/MDBox";
import api from "services/api";
import { useSnackbar } from "context/SnackbarContext";
import { useCompany } from "context/CompanyContext";
import { useTable, useSortBy, usePagination, useRowSelect } from "react-table";
import FormControl from "@mui/material/FormControl";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import InputLabel from "@mui/material/InputLabel";
import Pagination from "@mui/material/Pagination";
import warehouseTableData from "./warehouseTableData";
import Loader from "components/Loader/Loader";
import WarehouseItemModal from "./WarehouseItemModal";
import { useLanguage } from "context/LanguageContext";

const WarehouseItemList = ({ refreshTrigger }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pageCount, setPageCount] = useState(0);
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const { selectedCompany } = useCompany();
  const showSnackbar = useSnackbar();
  const { t } = useLanguage();

  const handleSuccess = () => {
    fetchWarehouseItems(pageIndex, pageSize);
  };

  /**
   * Fetches warehouse items from the backend.
   */
  const fetchWarehouseItems = useCallback(
    async (currentPage, currentSize) => {
      if (!selectedCompany) return;
      setLoading(true);

      const apiUrl = `/api/warehouse/items/?company_id=${selectedCompany.id}&page=${
        currentPage + 1
      }&page_size=${currentSize}`;
      console.log("Fetching warehouse items from:", apiUrl);

      try {
        const response = await api.get(apiUrl);
        const result = response.data;

        if (result.success) {
          const warehouseItemsWithId = result.items.map((item) => ({
            ...item,
            id: item.id,
          }));
          console.log("Fetched Warehouse Items:", warehouseItemsWithId);
          setRows(warehouseItemsWithId);
          setPageCount(Math.ceil(result.total_items / currentSize));
        } else {
          showSnackbar(`Error: ${result.error}`, "error");
        }
      } catch (error) {
        console.error("Error fetching warehouse items:", error);
        showSnackbar(
          t("errorFetchingWarehouseItems") || "Error fetching warehouse items.",
          "error"
        );
      } finally {
        setLoading(false);
      }
    },
    [selectedCompany, showSnackbar]
  );

  const handleEdit = useCallback((item) => {
    setEditingItem(item);
    setIsModalOpen(true);
  }, []);

  /**
   * Handles deletion of a warehouse item.
   * @param {number} itemId - The ID of the item to delete.
   */
  const handleDelete = useCallback(
    async (itemId) => {
      if (!window.confirm(t("confirmDeleteItem"))) {
        return;
      }

      try {
        const response = await api.post(
          `/api/warehouse/delete/`,
          {
            item_id: itemId,
            company_id: selectedCompany.id,
          },
          {
            headers: {
              "Content-Type": "application/json",
            },
          }
        );

        if (response.data.success) {
          showSnackbar(t("warehouseItemDeletedSuccessfully"), "success");
          fetchWarehouseItems(pageIndex, pageSize);
        } else {
          showSnackbar(`Error: ${response.data.error}`, "error");
        }
      } catch (error) {
        console.error("Error deleting warehouse item:", error);
        showSnackbar(t("errorDeletingWarehouseItem") || "Error deleting warehouse item.", "error");
      }
    },
    [selectedCompany, showSnackbar, pageIndex, pageSize, fetchWarehouseItems]
  );

  /**
   * Fetch warehouse items when the selected company or refreshTrigger changes.
   */
  useEffect(() => {
    if (selectedCompany) {
      fetchWarehouseItems(pageIndex, pageSize);
    }
  }, [selectedCompany, fetchWarehouseItems, refreshTrigger, pageIndex, pageSize]);

  // Define table columns
  const columns = useMemo(
    () => warehouseTableData(handleDelete, handleEdit).columns,
    [handleDelete, handleEdit]
  );
  const data = useMemo(() => rows, [rows]);

  // Initialize the table instance
  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    page,
    state: { pageIndex: controlledPageIndex, pageSize: controlledPageSize },
    gotoPage,
    setPageSize: setControlledPageSize,
  } = useTable(
    {
      columns,
      data,
      manualPagination: true,
      pageCount: pageCount,
      initialState: { pageIndex, pageSize },
    },
    useSortBy,
    usePagination,
    useRowSelect
  );

  useEffect(() => {
    setPageIndex(controlledPageIndex);
    setControlledPageSize(pageSize);
  }, [controlledPageIndex, pageSize, setControlledPageSize]);

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingItem(null);
  };

  /**
   * Handles changes to the number of items displayed per page.
   * @param {object} event - The event object.
   */
  const handleItemsPerPageChange = (event) => {
    const newPageSize = Number(event.target.value);
    setPageSize(newPageSize);
    gotoPage(0);
  };

  const handlePageChange = (event, value) => {
    gotoPage(value - 1);
  };

  if (loading && !rows.length) return <Loader />;

  return (
    <>
      <MDBox pt={3} pb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card>
              <MDBox p={3}>
                {/* eslint-disable react/jsx-props-no-spreading */}
                <table {...getTableProps()} style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead>
                    {headerGroups.map((headerGroup, index) => (
                      <tr {...headerGroup.getHeaderGroupProps()} key={headerGroup.id || index}>
                        {headerGroup.headers.map((column, colIndex) => (
                          <th
                            {...column.getHeaderProps(column.getSortByToggleProps())}
                            key={column.id || colIndex}
                            style={{
                              borderBottom: "1px solid #ddd",
                              background: "#f4f4f4",
                              padding: "8px",
                              fontWeight: "bold",
                              textAlign: "left",
                              width: `${column.width || "20%"}`,
                            }}
                          >
                            {column.render("Header")}
                            <span>
                              {column.isSorted ? (column.isSortedDesc ? " 🔽" : " 🔼") : ""}
                            </span>
                          </th>
                        ))}
                      </tr>
                    ))}
                  </thead>
                  <tbody {...getTableBodyProps()}>
                    {page.map((row, rowIndex) => {
                      prepareRow(row);
                      return (
                        <tr {...row.getRowProps()} key={row.id || rowIndex}>
                          {row.cells.map((cell, cellIndex) => (
                            <td
                              {...cell.getCellProps()}
                              key={cell.column.id || cellIndex}
                              style={{
                                padding: "8px",
                                borderBottom: "1px solid #ddd",
                                textAlign: "left",
                                width: `${cell.column.width || "20%"}`,
                              }}
                            >
                              {cell.render("Cell")}
                            </td>
                          ))}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                {/* eslint-enable react/jsx-props-no-spreading */}

                <MDBox display="flex" justifyContent="space-between" alignItems="center" mt={3}>
                  <FormControl variant="outlined" style={{ minWidth: 120 }}>
                    <InputLabel id="items-per-page-label">{t("perPage")}</InputLabel>
                    <Select
                      labelId="items-per-page-label"
                      value={pageSize}
                      onChange={handleItemsPerPageChange}
                      label={t("perPage")}
                    >
                      {[20, 40, 100].map((pageSizeOption) => (
                        <MenuItem key={pageSizeOption} value={pageSizeOption}>
                          {t("showAll")} {pageSizeOption}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <Pagination
                    count={pageCount}
                    page={pageIndex + 1}
                    onChange={handlePageChange}
                    color="primary"
                    shape="rounded"
                  />
                </MDBox>
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
      <WarehouseItemModal
        open={isModalOpen}
        handleClose={handleCloseModal}
        onSuccess={handleSuccess}
        item={editingItem}
      />
    </>
  );
};

WarehouseItemList.propTypes = {
  refreshTrigger: PropTypes.bool.isRequired, // Added prop to trigger refresh
};

export default WarehouseItemList;
